/**
 * Height maths for the Indy composer's field.
 *
 * The composer sizes its own textarea rather than using Ant Design's
 * `autoSize`. rc-textarea measures a hidden clone: a single offscreen
 * `<textarea>` shared by every antd field in the portal, styled by copying 18
 * named properties off the real one (`calculateNodeHeight.js`, SIZING_STYLE).
 * `scrollbar-gutter` is not one of them, and the clone is pinned to
 * `overflow: hidden` so nothing can narrow it — so our reserved gutter shrank
 * the field the writer types in without shrinking the ruler. The clone wrapped
 * ~15px later than the field did, and for the characters in between the field
 * was visibly two lines while sized for one, with the overflow clipped out of
 * sight. Long unbroken strings showed it worst, because they can break at every
 * character rather than at spaces.
 *
 * Measuring the real element ends that whole class of bug: there is no copy to
 * drift. It also means the next thing we do to the field's content box — an
 * inset, a border, different padding — needs no counterpart here.
 */

export interface FieldMetrics {
  /**
   * `scrollHeight` read with the height released to zero, so it reports what
   * the content wants rather than what the box currently allows. Includes
   * padding, excludes borders. Zero and not `auto`: `auto` on a textarea falls
   * back to its `rows` attribute, which floors the reading at two lines.
   */
  contentHeight: number;
  /** Resolved `line-height` in px. Whole pixels — see ChatComposer.css. */
  lineHeight: number;
  /** padding-top + padding-bottom. */
  padding: number;
  /** border-top + border-bottom. */
  border: number;
  minRows: number;
  maxRows: number;
}

export interface FieldHeight {
  /** Height to apply, clamped between the row bounds. */
  height: number;
  /** At the cap with content still under it — the only state showing a bar. */
  overflowing: boolean;
}

/** Border-box height of a field holding exactly `rows` lines. */
const rowsHeight = (
  rows: number,
  { lineHeight, padding, border }: FieldMetrics,
): number => rows * lineHeight + padding + border;

export const fieldHeight = (metrics: FieldMetrics): FieldHeight => {
  const min = rowsHeight(metrics.minRows, metrics);
  const max = rowsHeight(metrics.maxRows, metrics);
  // `contentHeight` is a scrollHeight, which stops at the padding edge; the
  // applied height is border-box.
  const natural = metrics.contentHeight + metrics.border;
  return {
    height: Math.min(Math.max(natural, min), max),
    // A pixel of tolerance: sub-pixel content leaves scrollHeight a hair over
    // the cap on a field that is exactly full, and reacting to that showed a
    // scrollbar on a message that fits.
    overflowing: natural - max > 1,
  };
};
